/**
 * Global query key factory.
 * Feature modules may extend with local keys but should nest under these roots.
 */
export const queryKeys = {
  auth: {
    root: ['auth'] as const,
  },
  dashboard: {
    root: ['dashboard'] as const,
    campaignCount: () => [...queryKeys.dashboard.root, 'campaign-count'] as const,
  },
  campaigns: {
    root: ['campaigns'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.campaigns.root, 'list', filters ?? {}] as const,
    cities: (search = '') =>
      [...queryKeys.campaigns.root, 'cities', search] as const,
  },
  billboards: {
    root: ['billboards'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.billboards.root, 'list', filters ?? {}] as const,
    detail: (id: string) => [...queryKeys.billboards.root, 'detail', id] as const,
    nearby: (params?: Record<string, unknown>) =>
      [...queryKeys.billboards.root, 'nearby', params ?? {}] as const,
  },
  locations: {
    root: ['locations'] as const,
    cities: (search = '') =>
      [...queryKeys.locations.root, 'cities', search] as const,
  },
  testimonials: {
    root: ['testimonials'] as const,
    list: () => [...queryKeys.testimonials.root, 'list'] as const,
  },
  analytics: {
    root: ['analytics'] as const,
    platformStats: () => [...queryKeys.analytics.root, 'platform-stats'] as const,
  },
  tutorials: {
    root: ['tutorials'] as const,
    list: () => [...queryKeys.tutorials.root, 'list'] as const,
  },
  wallet: {
    root: ['wallet'] as const,
    balance: () => [...queryKeys.wallet.root, 'balance'] as const,
  },
  notifications: {
    root: ['notifications'] as const,
    list: () => [...queryKeys.notifications.root, 'list'] as const,
    unreadCount: () =>
      [...queryKeys.notifications.root, 'unread-count'] as const,
  },
  appStatus: {
    root: ['appStatus'] as const,
    popupConfig: () => [...queryKeys.appStatus.root, 'popup-config'] as const,
  },
  coupons: {
    root: ['coupons'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.coupons.root, 'list', filters ?? {}] as const,
    available: (amount: number) =>
      [...queryKeys.coupons.root, 'available', amount] as const,
  },
  gallery: {
    root: ['gallery'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.gallery.root, 'list', filters ?? {}] as const,
    counts: () => [...queryKeys.gallery.root, 'counts'] as const,
  },
  policies: {
    root: ['policies'] as const,
    slugs: () => [...queryKeys.policies.root, 'slugs'] as const,
    content: (slug: string) =>
      [...queryKeys.policies.root, 'content', slug] as const,
  },
  support: {
    root: ['support'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.support.root, 'list', filters ?? {}] as const,
    detail: (id: string) => [...queryKeys.support.root, 'detail', id] as const,
    messages: (id: string) =>
      [...queryKeys.support.root, 'messages', id] as const,
  },
  clients: {
    root: ['clients'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.clients.root, 'list', filters ?? {}] as const,
    detail: (id: string) => [...queryKeys.clients.root, 'detail', id] as const,
  },
  team: {
    root: ['team'] as const,
    createFields: () => [...queryKeys.team.root, 'create-fields'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.team.root, 'list', filters ?? {}] as const,
    detail: (id: string) => [...queryKeys.team.root, 'detail', id] as const,
    assignedClients: (id: string) =>
      [...queryKeys.team.root, 'assigned-clients', id] as const,
  },
  wishlist: {
    root: ['wishlist'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.wishlist.root, 'list', filters ?? {}] as const,
  },
  reports: {
    root: ['reports'] as const,
    campaignsDropdown: () => [...queryKeys.reports.root, 'campaigns-dropdown'] as const,
    list: () => [...queryKeys.reports.root, 'list'] as const,
    tabsData: (token: string, activeTab: string) =>
      [...queryKeys.reports.root, 'tabs-data', token, activeTab] as const,
  },
  partner: {
    root: ['partner'] as const,
  },
} as const;
